import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Ticket, Users, Trash2, Loader2 } from 'lucide-react';

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', plan: 'premium', days: '30', max_uses: '' });

  const token = localStorage.getItem('funnely_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadCodes = () => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/promo-codes`, { headers })
      .then(r => r.json())
      .then(d => { setCodes(d.promo_codes || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadCodes(); }, []);

  const handleCreate = async () => {
    if (!form.code.trim()) { toast.error('Code is required'); return; }
    setCreating(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/promo-codes`, {
        method: 'POST', headers, body: JSON.stringify(form)
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      toast.success('Promo code created');
      setCreateOpen(false);
      setForm({ code: '', plan: 'premium', days: '30', max_uses: '' });
      loadCodes();
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setCreating(false); }
  };

  const toggleActive = async (code, currentActive) => {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/promo-codes/${code}`, {
      method: 'PATCH', headers, body: JSON.stringify({ active: !currentActive })
    });
    loadCodes();
    toast.success(`${code} ${!currentActive ? 'activated' : 'deactivated'}`);
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`Delete promo code ${code}?`)) return;
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/promo-codes/${code}`, {
      method: 'DELETE', headers
    });
    loadCodes();
    toast.success('Deleted');
  };

  if (loading) return <div className="space-y-4"><div className="h-10 w-48 bg-muted animate-pulse rounded" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-promo-codes">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#195259' }}>Promo Codes</h1>
          <p className="text-sm text-muted-foreground mt-1">{codes.length} promo code{codes.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-promo-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New Code
        </Button>
      </div>

      {/* Promo Code Cards */}
      <div className="space-y-4">
        {codes.map(promo => (
          <Card key={promo.code}>
            <CardContent className="pt-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: promo.active ? '#FF9A0020' : '#e5e5e5' }}>
                    <Ticket className="h-5 w-5" style={{ color: promo.active ? '#FF9A00' : '#999' }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-lg tracking-wider" style={{ color: '#195259' }}>{promo.code}</p>
                      <Badge className={promo.active ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'}>
                        {promo.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {promo.plan} plan &middot; {promo.days} days &middot; {promo.max_uses ? `Max ${promo.max_uses} uses` : 'Unlimited'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={promo.active} onCheckedChange={() => toggleActive(promo.code, promo.active)} />
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteCode(promo.code)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Usage Stats */}
              <div className="mt-4 flex items-center gap-6 text-sm">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Users className="h-3.5 w-3.5" /> {promo.used_count} used
                </span>
              </div>

              {/* Users who used this code */}
              {promo.users && promo.users.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Used by:</p>
                  <div className="flex flex-wrap gap-2">
                    {promo.users.map(u => (
                      <Badge key={u.id || u.email} variant="secondary" className="text-xs">
                        {u.username || u.email}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Promo Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Code</Label>
              <Input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="e.g. LAUNCH50" data-testid="promo-code-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Plan</Label>
                <Select value={form.plan} onValueChange={v => setForm(p => ({ ...p, plan: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Days</Label>
                <Input type="number" value={form.days} onChange={e => setForm(p => ({ ...p, days: e.target.value }))} placeholder="30" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Max Uses <span className="text-muted-foreground text-xs">(leave empty for unlimited)</span></Label>
              <Input type="number" value={form.max_uses} onChange={e => setForm(p => ({ ...p, max_uses: e.target.value }))} placeholder="Unlimited" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
